package br.com.vvrefeicoes.portal.domain.model

import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_expense_estivas
import portalvv.shared.generated.resources.operational_expense_gas
import portalvv.shared.generated.resources.operational_expense_hortifruti
import portalvv.shared.generated.resources.operational_expense_laticinios
import portalvv.shared.generated.resources.operational_expense_nao_alimentar_central
import portalvv.shared.generated.resources.operational_expense_nao_alimentar_operacao
import portalvv.shared.generated.resources.operational_expense_padarias
import portalvv.shared.generated.resources.operational_expense_proteinas
import portalvv.shared.generated.resources.operational_expense_sucos_bebidas
import portalvv.shared.generated.resources.operational_row_faturamento_receita
import portalvv.shared.generated.resources.operational_row_ticket
import portalvv.shared.generated.resources.operational_row_transportados
import portalvv.shared.generated.resources.operational_table_faturamento

/**
 * Catálogo das tabelas do lançamento mensal.
 *
 * A primeira tabela é a de faturamento (receita), com linhas fixas. As demais são
 * as categorias de despesa, cujas linhas são fornecedores adicionados pelo usuário.
 */
object OperationalEntryCatalog {
    const val FATURAMENTO_TABLE_ID = "faturamento"
    const val ROW_FATURAMENTO_ID = "faturamento-receita"
    const val ROW_TICKET_ID = "ticket"
    const val ROW_TRANSPORTADOS_ID = "transportados"

    const val PROTEINAS_TABLE_ID = "proteinas"
    const val ESTIVAS_TABLE_ID = "estivas"
    const val HORTIFRUTI_TABLE_ID = "hortifruti"
    const val LATICINIOS_TABLE_ID = "laticinios"
    const val PADARIAS_TABLE_ID = "padarias"
    const val SUCOS_BEBIDAS_TABLE_ID = "sucos-e-bebidas"
    const val GAS_TABLE_ID = "gas"
    const val NAO_ALIMENTAR_CENTRAL_TABLE_ID = "nao-alimentar-central"
    const val NAO_ALIMENTAR_OPERACAO_TABLE_ID = "nao-alimentar-operacao"

    val revenueTable: EntryTable =
        EntryTable(
            id = FATURAMENTO_TABLE_ID,
            title = Res.string.operational_table_faturamento,
            kind = EntryTableKind.REVENUE,
            rows =
                listOf(
                    EntryRow(id = ROW_FATURAMENTO_ID, label = EntryRowLabel.Resource(Res.string.operational_row_faturamento_receita)),
                    EntryRow(id = ROW_TICKET_ID, label = EntryRowLabel.Resource(Res.string.operational_row_ticket)),
                    EntryRow(id = ROW_TRANSPORTADOS_ID, label = EntryRowLabel.Resource(Res.string.operational_row_transportados)),
                ),
        )

    val expenseTables: List<EntryTable> =
        listOf(
            expense(PROTEINAS_TABLE_ID, Res.string.operational_expense_proteinas),
            expense(ESTIVAS_TABLE_ID, Res.string.operational_expense_estivas),
            expense(HORTIFRUTI_TABLE_ID, Res.string.operational_expense_hortifruti),
            expense(LATICINIOS_TABLE_ID, Res.string.operational_expense_laticinios),
            expense(PADARIAS_TABLE_ID, Res.string.operational_expense_padarias),
            expense(SUCOS_BEBIDAS_TABLE_ID, Res.string.operational_expense_sucos_bebidas),
            expense(GAS_TABLE_ID, Res.string.operational_expense_gas),
            expense(NAO_ALIMENTAR_CENTRAL_TABLE_ID, Res.string.operational_expense_nao_alimentar_central),
            expense(NAO_ALIMENTAR_OPERACAO_TABLE_ID, Res.string.operational_expense_nao_alimentar_operacao),
        )

    val tables: List<EntryTable> = listOf(revenueTable) + expenseTables

    fun newEntry(
        companyId: Int,
        companyName: String,
        period: MonthPeriod,
        weekCount: Int,
        kind: OperationalEntryKind = OperationalEntryKind.GESTAO_FINANCEIRA,
        adminOthersPercent: Double = DEFAULT_ADMIN_OTHERS_PERCENT,
        unidadeId: String = "",
    ): OperationalEntry =
        OperationalEntry(
            id = "$companyId-${kind.name}-${period.id}",
            unidadeId = unidadeId,
            kind = kind,
            companyId = companyId,
            companyName = companyName,
            period = period,
            tables = emptyTables(weekCount),
            adminOthersPercent = adminOthersPercent,
        )

    fun emptyTables(weekCount: Int): List<EntryTable> =
        tables.map { table ->
            table.copy(
                rows = table.rows.map { row -> row.copy(weeks = List(weekCount) { null }) },
            )
        }

    private fun expense(
        id: String,
        title: StringResource,
    ): EntryTable =
        EntryTable(
            id = id,
            title = title,
            kind = EntryTableKind.EXPENSE,
        )
}
